import cx from "classnames";
import { Form, FormContainer } from "../../../../components/form/Form";
import { ScrollPositionAnchor } from "../../../../components/scrollPosition/ScrollPositionAnchor";
import { SectionFieldSet } from "../../../../components/sectionFieldSet/SectionFieldSet";
import { LinkAnchors } from "../ContractEdit";
import { FormName } from "../menus/ContractEditMenu";
import { useContractId } from "../../../../hooks/useContractId";
import { useContract } from "../../../../hooks/useContract";
import { isContractDisabled } from "../../../../App";
import {
  FunctionComponent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useMutation,
  useQueryClient,
  useSuspenseQueries,
} from "@tanstack/react-query";
import { TextArea } from "../../../../components/form/TextArea";
import { hasRealErrors } from "../../../../components/form/FormHelpers";
import { ContractEditError } from "../ContractEditError";
import {
  contractErrorState,
  contractSaveState,
} from "../../../../state/contractSaveState";
import { useSetAtom } from "jotai";
import { ContractSaveError, handleError } from "../ContractPage";
import {
  dataFinancial,
  FinancialData,
  isFinancialDataComplete,
} from "../../../../data/dataFinancial";
import { ServerError } from "../../../../network/API";
import { UNAUTHORIZED_STATUS_CODE } from "../../../../data/data";
import { ONGOING_RESPONSE } from "../../../../data/models/CommonTypes";
import { RequiredValidator } from "../../../../components/form/validators/RequiredValidator";
import { MaxLengthValidator } from "../../../../components/form/validators/MaxLengthValidator";
import { Trans, useTranslation } from "react-i18next";
import styles from "./FinancialEstimates.module.scss";
import { isEqual } from "../../../../components/utils";
import { Select } from "../../../../components/form/Select";
import { Language } from "../../../../i18n";
import { getMaxValueOpts, getTurnoverOpts } from "./FinancialEstimatesUtils";
import { ButtonPane } from "../../../../components/interactions/Buttons/ButtonPane";
import { LabelWrapper } from "../../../../components/labelWrapper/LabelWrapper";
import { InfoBox } from "../../../../components/boxes/InfoBox";
import { T } from "../../../../components/translation/T";
import { Toggle } from "../../../../components/animate/Toggle";
import { TextInput } from "../../../../components/form/TextInput";
import { financialDataQueue } from "../../../../data/queues/dataQueues";

const MAX_LENGTH_DESCRIPTION = 255;
const WORKDAYS_IN_A_YEAR = 300;

interface Props {
  id: number;
}

export const FinancialEstimates = ({ id }: Props) => {
  return (
    <SectionFieldSet
      headerTitle={LinkAnchors.FINANCIAL_ESTIMATES.name}
      formName={FormName.FINANCIAL_ESTIMATES}
      sectionNumber={id}
    >
      <FinancialEstimatesInner />
    </SectionFieldSet>
  );
};

const FinancialEstimatesInner: FunctionComponent = () => {
  const contractId = useContractId();
  const contract = useContract();
  const isDisabled = isContractDisabled(contract);
  const setDataError = useSetAtom(contractErrorState);
  const setDataSaved = useSetAtom(contractSaveState);
  const onClose = useCallback(() => setError(null), []);
  const { t, i18n } = useTranslation();
  const saveTimer = useRef<number>(undefined);
  const formRef = useRef<FormContainer>(undefined);
  const queryClient = useQueryClient();
  const queryKey = dataFinancial.getFinancialDataKey(contractId);

  const [{ data: financial }] = useSuspenseQueries({
    queries: [dataFinancial.fetchFinancialData(contractId)],
  });

  const setFinancial = useCallback(
    (update: Partial<FinancialData>) => {
      queryClient.setQueryData(queryKey, update);
    },
    [queryClient, queryKey]
  );

  const [error, setError] = useState<ContractSaveError | null>(null);
  const lastEditedField = useRef<string>("");
  const latestSaved = useRef<FinancialData>(window.structuredClone(financial));
  const latestErroneousSaved = useRef<FinancialData | null>(null);
  const [displayableError, setDisplayableError] = useState<
    string | undefined
  >();

  const { mutate: trySave } = useMutation({
    mutationFn: (financial: FinancialData) => {
      setDisplayableError(undefined);

      if (isEqual(financial, latestSaved.current)) {
        return Promise.resolve(financial);
      }

      return financialDataQueue.saveData(contractId, {
        ...financial,
        thirdPartyPayments: false,
        estimatedRangeOfTransactionValueMin: 0,
        licensingBodyName: financial.licensedEntity
          ? financial.licensingBodyName
          : "",
      });
    },
    onSuccess: (response) => {
      setFinancial(response as FinancialData);

      latestSaved.current = response as FinancialData;
      setDataSaved((dataSaved) =>
        dataSaved.concat({
          date: new Date(),
        })
      );
    },
    onError: (err: unknown, updated: FinancialData, context) => {
      if (err === ONGOING_RESPONSE) {
        return;
      }

      if (
        (err as ServerError<FinancialData>).status === UNAUTHORIZED_STATUS_CODE
      ) {
        return;
      }

      const untypedError = err as any;

      if (typeof untypedError?.data === "string") {
        setDisplayableError(untypedError?.data);
      } else if (typeof untypedError?.data?.message === "string") {
        setDisplayableError(untypedError?.data?.message);
      } else {
        setDisplayableError(undefined);
      }

      latestErroneousSaved.current = financial;
      handleError(err as ServerError<FinancialData>, setError);
      setFinancial(latestSaved.current);
      setDataError((dataErrors) =>
        dataErrors.concat({
          date: new Date(),
        })
      );
    },
  });

  const onChange = useCallback(
    (value: string | number, name: string) => {
      lastEditedField.current = name;

      setFinancial({
        ...financial,
        [name]: value,
      });
    },
    [setFinancial, financial]
  );

  const retrySave = useCallback(() => {
    if (!latestErroneousSaved.current) {
      return;
    }

    setError(null);

    setTimeout(() => {
      if (!latestErroneousSaved.current) {
        return;
      }

      trySave(latestErroneousSaved.current);
    }, 500);
  }, [trySave]);

  const saveFinancial = useCallback(
    (copy?: FinancialData) => {
      clearTimeout(saveTimer.current);

      if (!isFinancialDataComplete(financial)) {
        return;
      }

      saveTimer.current = window.setTimeout(() => {
        // formRef.current?.log();
        if (!formRef.current?.isValid) {
          return;
        }
        trySave(copy ?? financial);
      }, 1000);
    },
    [trySave, financial]
  );

  const onChangeSave = useCallback(
    (value: number, name: string) => {
      lastEditedField.current = name;

      const copy = { ...financial, [name]: value };

      setFinancial(copy);
      saveFinancial(copy);
    },
    [setFinancial, saveFinancial, financial]
  );

  const opts = useMemo(
    () => getTurnoverOpts(t, i18n.language as Language, contract.country),
    [contract.country, i18n.language, t]
  );

  return (
    <div>
      <ScrollPositionAnchor id={LinkAnchors.FINANCIAL_ESTIMATES.anchor} />
      <ContractEditError
        error={error}
        displayableError={displayableError}
        retry={latestErroneousSaved.current ? retrySave : undefined}
        onClose={onClose}
      />
      <Form
        name={FormName.FINANCIAL_ESTIMATES}
        onSaveTrigger={(_, form) => {
          const realErrors = hasRealErrors(form);

          if (realErrors) {
            return;
          }

          saveFinancial();
        }}
        formContainer={formRef}
      >
        <div className="small m-top-20">
          <InfoBox>
            <div className={styles.warning}>
              <Trans t={t}>
                These texts/values will be exposed to the merchant. Please
                answer the applicable questions in the{" "}
                <b>language of the merchant</b>.
              </Trans>
            </div>
          </InfoBox>
        </div>
        <div className="m-top-50">
          <TextArea
            className={styles.desc}
            disabled={isDisabled}
            label="How would the merchant describe the company's core business?"
            placeholder={t(
              'E.g. "Operates a lunch restaurant that sells home cooked food and drinks with à la carte in the evenings."'
            )}
            onChange={onChange}
            value={financial.businessModel}
            name="businessModel"
            validators={[
              new RequiredValidator("Value is required"),
              new MaxLengthValidator(
                MAX_LENGTH_DESCRIPTION,
                `Description must not exceed ${MAX_LENGTH_DESCRIPTION} characters`
              ),
            ]}
          />
        </div>

        <div className="m-top-10">
          <TextArea
            disabled={isDisabled}
            label="What are the most common goods/services company of the merchant sells?"
            placeholder={t('E.g. "We sell lunch with accompanying drink"')}
            onChange={onChange}
            value={financial.productDescription}
            name="productDescription"
            validators={[new RequiredValidator("Value is required")]}
          />
        </div>

        <div className={cx(styles.question, "m-bottom-30")}>
          <Select
            onChange={onChangeSave}
            name="estimatedRangeOfTransactionValueMax"
            label={t("What does the merchant's most expensive item cost?")}
            alternatives={getMaxValueOpts(
              t,
              i18n.language as Language,
              contract.country
            )}
            value={financial.estimatedRangeOfTransactionValueMax}
            validators={[
              new RequiredValidator("Cost of most expensive item is required"),
            ]}
          />
        </div>

        <div className={cx(styles.question)}>
          <Select
            onChange={onChangeSave}
            name="estimatedAnnualTurnover"
            label={t(
              "Approximately, what is the total turnover of the merchant's company per year?"
            )}
            alternatives={opts}
            value={financial.estimatedAnnualTurnover}
            validators={[
              new RequiredValidator("Estimated annual turnover is required"),
            ]}
          />
        </div>

        <div className={cx(styles.question, "m-bottom-40")}>
          <LabelWrapper
            label={t(
              "Give us an indication of the number of paying customers the merchant expects per day, including all payment methods"
            )}
            required
            value={financial.estimatedTransactionsPerYear}
          >
            <ButtonPane
              className={styles.pane}
              // required="Paying customers per day is required"
              onClick={(value) => {
                setFinancial({
                  ...financial,
                  estimatedTransactionsPerYear: value.value,
                });
              }}
              buttons={[
                {
                  active:
                    financial.estimatedTransactionsPerYear ===
                    WORKDAYS_IN_A_YEAR,
                  text: t("0 - 2"),
                  data: {
                    value: 1 * WORKDAYS_IN_A_YEAR,
                  },
                },
                {
                  active:
                    financial.estimatedTransactionsPerYear ===
                    12 * WORKDAYS_IN_A_YEAR,
                  text: t("3 - 20"),
                  data: {
                    value: 12 * WORKDAYS_IN_A_YEAR,
                  },
                },
                {
                  active:
                    financial.estimatedTransactionsPerYear ===
                    60 * WORKDAYS_IN_A_YEAR,
                  text: t("21 - 100"),
                  data: {
                    value: 60 * WORKDAYS_IN_A_YEAR,
                  },
                },
                {
                  active:
                    financial.estimatedTransactionsPerYear ===
                    150 * WORKDAYS_IN_A_YEAR,
                  text: t("above 100"),
                  data: {
                    value: 150 * WORKDAYS_IN_A_YEAR,
                  },
                },
              ]}
            />
          </LabelWrapper>
        </div>

        <div className={cx(styles.question, "m-bottom-40")}>
          <LabelWrapper
            label={t(
              "Approximately, how many of the merchant's customers are paying by card?"
            )}
            required
            value={financial.estimatedTransactionsByCardPerYear}
          >
            <ButtonPane
              className={cx(styles.pane)}
              // required="Number of customers paying by card is required"
              onClick={(value) => {
                setFinancial({
                  ...financial,
                  estimatedTransactionsByCardPerYear: value.value,
                });
              }}
              buttons={[
                {
                  active: financial.estimatedTransactionsByCardPerYear === 13,
                  text: t("0 - 25%"),
                  data: {
                    value: 13,
                  },
                },
                {
                  active: financial.estimatedTransactionsByCardPerYear === 38,
                  text: t("26 - 50%"),
                  data: {
                    value: 38,
                  },
                },
                {
                  active: financial.estimatedTransactionsByCardPerYear === 63,
                  text: t("51 - 75%"),
                  data: {
                    value: 63,
                  },
                },
                {
                  active: financial.estimatedTransactionsByCardPerYear === 90,
                  text: t("above 75%"),
                  data: {
                    value: 90,
                  },
                },
              ]}
            />
          </LabelWrapper>
        </div>

        <hr />

        <LabelWrapper
          label={t("Is a license required to run the merchant's business?")}
          required
          value={financial.licensedEntity}
        >
          <ButtonPane
            onClick={(value) => {
              setFinancial({
                ...financial,
                licensedEntity: value.value,
              });
            }}
            buttons={[
              {
                text: "Yes",
                active: financial.licensedEntity === true,
                data: {
                  value: true,
                },
              },
              {
                text: "No",
                active: financial.licensedEntity === false,
                data: {
                  value: false,
                },
              },
            ]}
          />
        </LabelWrapper>

        <div className="small m-top-20">
          <InfoBox>
            <T>
              Examples of businesses that require a license are: dentists, taxis
              and casinos.
            </T>
          </InfoBox>
        </div>

        <div className="m-top-20">
          <Toggle open={financial.licensedEntity}>
            <div className={styles.licensedEntity}>
              <TextInput
                name="licensingBodyName"
                label="Name of the licensing body"
                value={financial.licensingBodyName}
                onChange={onChange}
                validators={
                  financial.licensedEntity
                    ? [
                        new RequiredValidator(
                          "Name of licensing body is required"
                        ),
                      ]
                    : []
                }
              />
            </div>
          </Toggle>
        </div>
      </Form>
    </div>
  );
};
